import React from 'react'
import {
  Box,
  Typography,
  LinearProgress,
  Paper,
  Button,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
} from '@mui/material'
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Refresh as RetryIcon,
  HourglassEmpty as WaitingIcon,
} from '@mui/icons-material'

const INGESTION_STEPS = [
  { key: 'extract', label: 'Extracting Text', description: 'Reading and extracting text from uploaded documents' },
  { key: 'analyze', label: 'AI Analysis', description: 'Analyzing content and generating module structure' },
  { key: 'kb_create', label: 'Creating Knowledge Base', description: 'Creating Dify knowledge base for RAG' },
  { key: 'upload', label: 'Uploading Documents', description: 'Uploading content to knowledge base' },
  { key: 'db_create', label: 'Creating Module', description: 'Creating module record in database' },
]

// Map progress percentage to step
const getStepFromProgress = (progress, status) => {
  if (status === 'analyzing') {
    if (progress < 30) return 0
    return 1
  }
  if (status === 'ingesting') {
    if (progress < 55) return 2
    if (progress < 85) return 3
    return 4
  }
  if (['completed', 'published'].includes(status)) {
    return INGESTION_STEPS.length
  }
  return 0
}

function IngestionProgress({ status, progress, currentStep, error, onRetry }) {
  const isProcessing = ['analyzing', 'ingesting'].includes(status)
  const isFailed = ['analysis_failed', 'ingestion_failed'].includes(status)
  const isComplete = ['completed', 'published'].includes(status)
  const activeStep = getStepFromProgress(progress, status)

  return (
    <Box>
      {/* Progress Bar */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          {isProcessing && <CircularProgress size={24} />}
          {isFailed && <ErrorIcon color="error" />}
          {isComplete && <CheckIcon color="success" />}
          <Typography variant="h6">
            {isProcessing && 'Processing...'}
            {isFailed && 'Processing Failed'}
            {isComplete && 'Processing Complete'}
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progress || 0}
          color={isFailed ? 'error' : isComplete ? 'success' : 'primary'}
          sx={{ height: 10, borderRadius: 1, mb: 1 }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {currentStep || 'Initializing...'}
          </Typography>
          <Typography variant="body2" fontWeight="medium">
            {progress || 0}%
          </Typography>
        </Box>
      </Paper>

      {/* Error Alert */}
      {isFailed && error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            onRetry && (
              <Button
                color="inherit"
                size="small"
                startIcon={<RetryIcon />}
                onClick={onRetry}
              >
                Retry
              </Button>
            )
          }
        >
          <Typography variant="body2" fontWeight="medium">
            {error}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            You can retry the operation or go back to make changes.
          </Typography>
        </Alert>
      )}

      {/* Step Details */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Processing Steps
        </Typography>
        <Stepper activeStep={activeStep} orientation="vertical">
          {INGESTION_STEPS.map((step, index) => {
            const isActive = index === activeStep && isProcessing
            const isCompleted = index < activeStep || isComplete
            const isError = isFailed && index === activeStep

            return (
              <Step key={step.key} completed={isCompleted}>
                <StepLabel
                  error={isError}
                  StepIconComponent={() => {
                    if (isError) return <ErrorIcon color="error" fontSize="small" />
                    if (isCompleted) return <CheckIcon color="success" fontSize="small" />
                    if (isActive) return <CircularProgress size={20} />
                    return (
                      <WaitingIcon
                        fontSize="small"
                        sx={{ color: 'action.disabled' }}
                      />
                    )
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={isActive ? 'medium' : 'normal'}
                    color={isError ? 'error' : isActive ? 'text.primary' : 'text.secondary'}
                  >
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="caption" color="text.secondary">
                    {step.description}
                  </Typography>
                </StepContent>
              </Step>
            )
          })}
        </Stepper>
      </Paper>

      {/* Processing Tips */}
      {isProcessing && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            This process may take a few minutes depending on the size of your documents.
            You can leave this page and return later - processing will continue in the background.
          </Typography>
        </Alert>
      )}
    </Box>
  )
}

export default IngestionProgress
